import { Controller } from '@hotwired/stimulus'
import { i18n } from '../lib/i18n'

// Manages enum value editing for report data fields
// Provides both guided mode (value/label rows) and raw JSON mode
export default class extends Controller {
  static targets = ['list', 'hidden', 'guidedMode', 'jsonMode', 'jsonInput', 'modeLabel']
  static values = { initial: String }

  connect() {
    this.isJsonMode = false
    this.values = []

    // Parse initial value
    if (this.initialValue) {
      try {
        const config = JSON.parse(this.initialValue)
        this.values = config.values || []
      } catch (e) {
        this.values = []
      }
    }

    this.renderList()
    this.syncHidden()
  }

  renderList() {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = this.values.map((item, index) => `
      <div class="flex gap-2 items-center" data-index="${index}">
        <input type="text"
               class="input input-bordered input-sm flex-1"
               placeholder="${i18n.t('enum_values.value')}"
               value="${this.escapeHtml(item.value || '')}"
               data-type="value"
               data-action="input->enum-values#updateValue">
        <input type="text"
               class="input input-bordered input-sm flex-1"
               placeholder="${i18n.t('enum_values.label')}"
               value="${this.escapeHtml(item.label || '')}"
               data-type="label"
               data-action="input->enum-values#updateValue">
        <button type="button"
                class="btn btn-ghost btn-sm btn-circle"
                data-action="click->enum-values#remove"
                data-index="${index}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `).join('')
  }

  add() {
    this.values.push({ value: '', label: '' })
    this.renderList()
    this.syncHidden()

    // Focus the new value input
    const inputs = this.listTarget.querySelectorAll('input[data-type="value"]')
    if (inputs.length > 0) {
      inputs[inputs.length - 1].focus()
    }
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this.values.splice(index, 1)
    this.renderList()
    this.syncHidden()
  }

  updateValue(event) {
    const row = event.currentTarget.closest('[data-index]')
    const index = parseInt(row.dataset.index)
    const type = event.currentTarget.dataset.type

    this.values[index][type] = event.currentTarget.value
    this.syncHidden()
  }

  toggleMode() {
    this.isJsonMode = !this.isJsonMode

    if (this.isJsonMode) {
      // Switch to JSON mode
      this.guidedModeTarget.classList.add('hidden')
      this.jsonModeTarget.classList.remove('hidden')
      this.modeLabelTarget.textContent = i18n.t('enum_values.show_guided')

      // Populate JSON textarea
      const config = this.values.length > 0 ? { values: this.values } : {}
      this.jsonInputTarget.value = JSON.stringify(config, null, 2)
    } else {
      // Switch to guided mode
      this.guidedModeTarget.classList.remove('hidden')
      this.jsonModeTarget.classList.add('hidden')
      this.modeLabelTarget.textContent = i18n.t('enum_values.show_json')

      // Parse JSON and update
      this.updateFromJson()
      this.renderList()
    }
  }

  updateFromJson() {
    try {
      const config = JSON.parse(this.jsonInputTarget.value || '{}')
      this.values = config.values || []
      this.syncHidden()
    } catch (e) {
      // Invalid JSON, don't update
    }
  }

  syncHidden() {
    // Filter out empty entries
    const filtered = this.values.filter(v => v.value || v.label)

    const config = filtered.length > 0 ? { values: filtered } : null
    this.hiddenTarget.value = config ? JSON.stringify(config) : ''
  }

  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
